import { Caption1, Spinner, makeStyles, mergeClasses, tokens } from "@fluentui/react-components";
import { useEffect, useRef, useState } from "react";
import { AppDialog, DialogActionsBar } from "@/components/app-dialog";
import { FuiButton } from "@/components/fui-control";
import {
  AVATAR_VIEW,
  AVATAR_ZOOM_MAX,
  AVATAR_ZOOM_MIN,
  displayedSize,
  drawAvatar,
  initialCrop,
  moveCrop,
  panByKey,
  zoomByKey,
  zoomCrop,
  type CropFrame,
} from "./avatar-crop";

const useStyles = makeStyles({
  layout: {
    display: "flex",
    alignItems: "flex-start",
    gap: tokens.spacingHorizontalM,
    minWidth: 0,
  },
  stack: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    minWidth: 0,
  },
  stage: {
    position: "relative",
    width: `${AVATAR_VIEW}px`,
    height: `${AVATAR_VIEW}px`,
    flexShrink: 0,
    overflow: "hidden",
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground3,
    cursor: "grab",
    touchAction: "none",
    ":hover": { border: `1px solid ${tokens.colorNeutralStroke1}` },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "2px",
    },
    ":active": { cursor: "grabbing" },
  },
  stageBusy: {
    opacity: 0.5,
    pointerEvents: "none",
  },
  image: {
    position: "absolute",
    maxWidth: "none",
    userSelect: "none",
    pointerEvents: "none",
  },
  previews: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: tokens.spacingVerticalS,
    minWidth: 0,
  },
  preview: {
    position: "relative",
    overflow: "hidden",
    flexShrink: 0,
    borderRadius: tokens.borderRadiusCircular,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground3,
  },
  zoom: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    minWidth: 0,
  },
  range: {
    flex: 1,
    minWidth: 0,
    accentColor: tokens.colorBrandBackground,
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "2px",
    },
  },
  hint: { color: tokens.colorNeutralForeground3 },
  error: { color: tokens.colorPaletteRedForeground1 },
  wait: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
});

function CropPreview({ url, frame, size, label }: { url: string; frame: CropFrame; size: number; label: string }) {
  const styles = useStyles();
  const shown = displayedSize(frame);
  const scale = size / AVATAR_VIEW;
  return (
    <div className={styles.preview} style={{ width: size, height: size }} role="img" aria-label={label}>
      <img
        className={styles.image}
        src={url}
        alt=""
        draggable={false}
        style={{
          left: frame.x * scale,
          top: frame.y * scale,
          width: shown.width * scale,
          height: shown.height * scale,
        }}
      />
    </div>
  );
}

/** 정사각으로 자른 뒤 적용한다. 결과 저장은 호출하는 쪽이 한다. */
export function AvatarCropDialog({
  source,
  onCancel,
  onApply,
}: {
  source: { url: string; width: number; height: number } | null;
  onCancel: () => void;
  onApply: (avatar: string) => void;
}) {
  const styles = useStyles();
  const stageRef = useRef<HTMLDivElement>(null);
  const drag = useRef<{ px: number; py: number; x: number; y: number } | null>(null);
  const [frame, setFrame] = useState<CropFrame | null>(null);
  const [seen, setSeen] = useState("");
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  if (source && seen !== source.url) {
    setSeen(source.url);
    setFrame(initialCrop(source.width, source.height));
    setReady(false);
    setBusy(false);
    setError("");
  }
  if (!source && (seen !== "" || frame != null)) {
    setSeen("");
    setFrame(null);
    setReady(false);
    setBusy(false);
    setError("");
  }

  useEffect(() => {
    if (!source) return;
    const image = new Image();
    image.onload = () => setReady(true);
    image.onerror = () => setError("이 브라우저에서는 사진을 열 수 없습니다");
    image.src = source.url;
    return () => {
      image.onload = null;
      image.onerror = null;
    };
  }, [source]);

  useEffect(() => {
    const node = stageRef.current;
    if (!node) return;
    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      const step = event.deltaY > 0 ? -0.08 : 0.08;
      setFrame((current) => (current ? zoomCrop(current, current.zoom + step) : current));
    };
    node.addEventListener("wheel", onWheel, { passive: false });
    return () => node.removeEventListener("wheel", onWheel);
  }, [source, ready]);

  const apply = async () => {
    if (!source || !frame || !ready) return;
    setBusy(true);
    setError("");
    try {
      const image = new Image();
      image.src = source.url;
      await image.decode();
      onApply(drawAvatar(image, frame));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "이 브라우저에서는 사진을 줄일 수 없습니다");
      setBusy(false);
    }
  };

  return (
    <AppDialog
      open={source != null}
      onClose={onCancel}
      title="사진 자르기"
      description="정사각으로 자릅니다."
      width="md"
      footer={
        <DialogActionsBar
          cancelLabel="취소"
          confirmLabel="적용"
          disabled={!ready || busy || error !== ""}
          onCancel={onCancel}
          onConfirm={() => void apply()}
        />
      }
    >
      {source && frame ? (
        <div className={styles.stack}>
        <div className={styles.layout}>
          <div
            ref={stageRef}
            className={mergeClasses(styles.stage, busy && styles.stageBusy)}
            tabIndex={0}
            role="application"
            aria-label="자를 위치"
            onPointerDown={(event) => {
              if (busy) return;
              event.currentTarget.setPointerCapture(event.pointerId);
              drag.current = { px: event.clientX, py: event.clientY, x: frame.x, y: frame.y };
            }}
            onPointerMove={(event) => {
              const start = drag.current;
              if (!start) return;
              setFrame((current) => (current ? moveCrop({ ...current, x: start.x, y: start.y }, event.clientX - start.px, event.clientY - start.py) : current));
            }}
            onPointerUp={() => {
              drag.current = null;
            }}
            onKeyDown={(event) => {
              if (!frame || busy) return;
              const panned = panByKey(frame, event.key, event.shiftKey);
              const zoomed = panned ? null : zoomByKey(frame, event.key);
              const next = panned ?? zoomed;
              if (!next) return;
              event.preventDefault();
              setFrame(next);
            }}
          >
            <img
              className={styles.image}
              src={source.url}
              alt=""
              draggable={false}
              style={{
                left: frame.x,
                top: frame.y,
                width: displayedSize(frame).width,
                height: displayedSize(frame).height,
              }}
            />
            {ready ? null : (
              <span className={styles.wait}>
                <Spinner size="tiny" aria-label="사진을 열고 있습니다" />
              </span>
            )}
          </div>
          <div className={styles.previews}>
            <Caption1 className={styles.hint}>미리보기</Caption1>
            <CropPreview url={source.url} frame={frame} size={96} label="큰 미리보기" />
            <CropPreview url={source.url} frame={frame} size={32} label="작은 미리보기" />
          </div>
        </div>
        <div className={styles.zoom}>
        <FuiButton
          appearance="subtle"
          density="dialog"
          disabled={!frame || frame.zoom <= AVATAR_ZOOM_MIN || busy}
          onClick={() => setFrame((current) => (current ? zoomCrop(current, current.zoom - 0.1) : current))}
        >
          축소
        </FuiButton>
        <input
          className={styles.range}
          type="range"
          min={AVATAR_ZOOM_MIN}
          max={AVATAR_ZOOM_MAX}
          step={0.01}
          value={frame?.zoom ?? 1}
          aria-label="확대"
          disabled={!frame || busy}
          onChange={(event) => setFrame((current) => (current ? zoomCrop(current, Number(event.target.value)) : current))}
        />
        <FuiButton
          appearance="subtle"
          density="dialog"
          disabled={!frame || frame.zoom >= AVATAR_ZOOM_MAX || busy}
          onClick={() => setFrame((current) => (current ? zoomCrop(current, current.zoom + 0.1) : current))}
        >
          확대
        </FuiButton>
      </div>
      <Caption1 className={styles.hint}>화살표로 옮깁니다. Shift와 함께면 10px입니다.</Caption1>
      {error ? <Caption1 className={styles.error}>{error}</Caption1> : null}
        </div>
      ) : null}
    </AppDialog>
  );
}
